import cloneDeep from 'lodash/cloneDeep';
import $ from 'jquery';
import React from 'react';
import ReactDOM from 'react-dom';
import { ExpandCollapseButtons } from 'common/components/ExpandCollapseButtons';
import { isTableVisualization } from 'common/visualizations/helpers/VifSelectors';
import { Vif } from 'common/visualizations/vif';
import { isPivotFeatureFlagOn } from 'common/visualizations/helpers/PivotHelpers';

/**
 * - Render the Group Bar UI
 * - Various helpers to manage Group Bar state and UI
 */

type UpdateVifCallback = (newVif: Vif) => void;

export const template = (): JQuery => {
  return $('<div>', { class: 'socrata-visualization-group-bar-container' });
};

const getHierarchyPathFromVif = (vif: Vif) => {
  const activeHierarchyId = vif.series?.[0]?.dataSource?.activeHierarchyId;
  return vif.series?.[0]?.dataSource?.hierarchies?.find((h) => h.id == activeHierarchyId);
};

/**
 *  Must have at least 1 row group or if pivoting 2 row groups for the expland button to be displayed.
 */
const allowExpandGroups = (pivotMode: boolean, numberOfGroupedColumns: number) => {
  if (pivotMode) {
    return numberOfGroupedColumns > 1;
  } else {
    return numberOfGroupedColumns > 0;
  }
};

export const renderExpandCollapse = (
  container: JQuery<HTMLElement>,
  vif: Vif,
  onVifUpdated: UpdateVifCallback,
) => {
  const $expandCollapseContainer = container.find('.socrata-visualization-group-bar-container');
  const isTable = isTableVisualization(vif);
  const hierarchy = getHierarchyPathFromVif(vif);
  const numberOfGroupedColumns =
    hierarchy?.columnConfigurations?.filter((config) => config?.isGrouping === true)?.length ?? 0;
  const pivotMode = isPivotFeatureFlagOn() && (hierarchy?.pivotMode ?? false);
  const expandGroupsAllowed = allowExpandGroups(pivotMode, numberOfGroupedColumns);

  if (!isTable || !expandGroupsAllowed) {
    if ($expandCollapseContainer[0]) {
      ReactDOM.unmountComponentAtNode($expandCollapseContainer[0]);
    }
    return;
  }

  const level = hierarchy?.agGridOpenNodeLevel ?? 0;
  const expandDisabled = pivotMode ? level >= numberOfGroupedColumns - 1 : false;

  const props = {
    level,
    expandDisabled,
    onNodeLevelChange: (levelArg?: number) => {
      handleExpandCollapse(vif, onVifUpdated, levelArg);
    },
  };

  ReactDOM.render(React.createElement(ExpandCollapseButtons, props), $expandCollapseContainer[0]);
};

const handleExpandCollapse = (vif: Vif, updateVif: UpdateVifCallback, level?: number) => {
  const newVif = cloneDeep(vif);
  const hierarchy = getHierarchyPathFromVif(newVif);

  if (hierarchy) {
    hierarchy.agGridOpenNodeLevel = level;
    updateVif(newVif);
  }
};
